import { FC, useCallback, useContext } from 'react';

import { layoutContext } from '@context/layout';
import { useRouter } from 'next/router';
import useLazyWorkspaceUrl from '@hooks/use-lazy-workspace-url';

import {
  PopupDescriptionContainer,
  PopupDescriptionContent,
  PopupDescriptionTopSection,
  PopupDescriptionTopSectionIcon,
  PopupDescriptionTopSectionText,
  PopupDescriptionTitle,
  PopupDescriptionText,
  PopupDescriptionButtonContainer,
} from '../DescriptionBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAddressCard } from '@fortawesome/pro-solid-svg-icons';
import { faStars } from '@fortawesome/pro-solid-svg-icons';

import { PopupButtonContainer, PopupButton, PopupButtonIcon, PopupButtonText } from '../EmulatedSideNavItems';
import { HighlightPopup } from '../HighlightPopup';
import { NavItems } from '@hooks/use-navigation-context';
import { PopupType, useHighlightPopupService } from '../HighlighPopupService';
import Button from '@components/Button/Button';

import styles from './TeamCardHighlightPopup.module.scss';

const TeamCardHighlightPopup: FC<React.PropsWithChildren<unknown>> = () => {
  const router = useRouter();
  const { state: navState } = useContext(layoutContext);
  const generateWorkspaceUrl = useLazyWorkspaceUrl();
  const teamCardsUrl = generateWorkspaceUrl(NavItems.Team);
  const { closePopup, nextPopups, updateNextPopups, updateCurrentPopup } = useHighlightPopupService();

  const handleClickCreateCard = useCallback(() => {
    router.push(teamCardsUrl);
    closePopup();
  }, [teamCardsUrl, closePopup, router]);

  const handleClosePopup = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const handleClickNext = useCallback(() => {
    const currentNextPopups = [...nextPopups];
    const nextPopup = currentNextPopups.shift();

    updateCurrentPopup((nextPopup as PopupType) ?? null);
    updateNextPopups([...currentNextPopups]);
  }, [nextPopups, updateCurrentPopup, updateNextPopups]);

  return (
    <HighlightPopup
      closeOnChangeRouter={false}
      showOverlay
      highlightId="team-card"
      delay={0}
      onPopupClose={handleClosePopup}
    >
      <PopupButtonContainer>
        <PopupButton handleClick={handleClickCreateCard}>
          <PopupButtonIcon>
            <FontAwesomeIcon icon={faAddressCard} />
          </PopupButtonIcon>
          {!navState.collapsed && <PopupButtonText>Team Cards</PopupButtonText>}
        </PopupButton>
      </PopupButtonContainer>
      <PopupDescriptionContainer>
        <PopupDescriptionContent>
          <PopupDescriptionTopSection>
            <PopupDescriptionTopSectionIcon>
              <FontAwesomeIcon icon={faStars} />
            </PopupDescriptionTopSectionIcon>
            <PopupDescriptionTopSectionText>Cards</PopupDescriptionTopSectionText>
          </PopupDescriptionTopSection>
          <PopupDescriptionTitle>Create digital business cards</PopupDescriptionTitle>
          <PopupDescriptionText>Make digital business cards for your team.</PopupDescriptionText>
          {nextPopups.length > 0 && (
            <PopupDescriptionButtonContainer>
              <div className={styles['next-btn-container']}>
                <Button onClick={handleClickNext}>Next</Button>
              </div>
            </PopupDescriptionButtonContainer>
          )}
        </PopupDescriptionContent>
      </PopupDescriptionContainer>
    </HighlightPopup>
  );
};

export { TeamCardHighlightPopup };
